import type React from "react";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

/* ================================
 *  問い合わせセクション（確認画面つき）
 * ================================ */
function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    inquiry_type: "",
    message: "",
    privacy_agreement: false,
  });

  const [step, setStep] = useState<"input" | "confirm" | "done">("input");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] =
    useState<"idle" | "success" | "error">("idle");

  // 入力変更
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, type } = e.target;
    const value =
      type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // 入力フォーム → 確認画面 へ
  const handleInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.privacy_agreement) {
      alert("プライバシーポリシーに同意してください。");
      return;
    }

    if (
      !formData.name ||
      !formData.email ||
      !formData.inquiry_type ||
      !formData.message
    ) {
      alert("必須項目を入力してください。");
      return;
    }

    if (formData.message.length > 500) {
      alert("お問い合わせ内容は500文字以内で入力してください。");
      return;
    }

    setStep("confirm");
  };

  // 確認画面 → send.php へ送信
  const handleFinalSubmit = async () => {
    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      const body = new URLSearchParams();
      body.append("name", formData.name);
      body.append("email", formData.email);
      body.append("phone", formData.phone);
      body.append("inquiry_type", formData.inquiry_type);
      body.append("message", formData.message);
      body.append(
        "privacy_agreement",
        formData.privacy_agreement ? "同意する" : "同意しない"
      );

      const response = await fetch("/send.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: body.toString(),
      });

      if (response.ok) {
        setSubmitStatus("success");
        setStep("done");
        setFormData({
          name: "",
          email: "",
          phone: "",
          inquiry_type: "",
          message: "",
          privacy_agreement: false,
        });
      } else {
        setSubmitStatus("error");
      }
    } catch (e) {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // もう一度入力へ
  const handleReset = () => {
    setStep("input");
    setSubmitStatus("idle");
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-4xl font-bold text-gray-900 text-center mb-4">
          採用・お問い合わせ
        </h2>
        <p className="text-center text-gray-600 mb-12">
          お気軽にご連絡ください
        </p>

        {/* ===== 入力ステップ ===== */}
        {step === "input" && (
          <form
            className="space-y-6"
            data-readdy-form
            id="contact-form"
            onSubmit={handleInputSubmit}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                  placeholder="山田太郎"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                  placeholder="example@email.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                電話番号
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                placeholder="090-1234-5678"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                お問い合わせ種別 <span className="text-red-500">*</span>
              </label>
              <select
                name="inquiry_type"
                required
                value={formData.inquiry_type}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all cursor-pointer"
              >
                <option value="">選択してください</option>
                <option value="service">サービスについて</option>
                <option value="recruitment">採用について</option>
                <option value="partnership">業務提携について</option>
                <option value="other">その他</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                お問い合わせ内容 <span className="text-red-500">*</span>
              </label>
              <textarea
                name="message"
                required
                maxLength={500}
                rows={6}
                value={formData.message}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none"
                placeholder="お問い合わせ内容をご記入ください（500文字以内）"
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">
                {formData.message.length}/500文字
              </p>
            </div>

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                name="privacy_agreement"
                id="privacy_agreement"
                required
                checked={formData.privacy_agreement}
                onChange={handleChange}
                className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
              />
              <label
                htmlFor="privacy_agreement"
                className="text-sm text-gray-700 cursor-pointer"
              >
                <Link
                  to="/privacy"
                  className="text-teal-600 hover:text-teal-700 underline"
                >
                  プライバシーポリシー
                </Link>
                に同意します <span className="text-red-500">*</span>
              </label>
            </div>

            <button
              type="submit"
              className="w-full bg-gray-900 text-white py-4 rounded-lg font-medium hover:bg-gray-800 transition-all whitespace-nowrap cursor-pointer"
            >
              確認画面へ
            </button>
          </form>
        )}

        {/* ===== 確認ステップ ===== */}
        {step === "confirm" && (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              入力内容の確認
            </h3>
            <p className="text-gray-600 mb-6">
              以下の内容で送信します。よろしければ「この内容で送信する」
              を押してください。
            </p>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
              <div>
                <div className="text-sm text-gray-500">お名前</div>
                <div className="font-medium text-gray-900">{formData.name}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">メールアドレス</div>
                <div className="font-medium text-gray-900">
                  {formData.email}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">電話番号</div>
                <div className="font-medium text-gray-900">
                  {formData.phone || "（未入力）"}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">お問い合わせ種別</div>
                <div className="font-medium text-gray-900">
                  {(() => {
                    switch (formData.inquiry_type) {
                      case "service":
                        return "サービスについて";
                      case "recruitment":
                        return "採用について";
                      case "partnership":
                        return "業務提携について";
                      case "other":
                        return "その他";
                      default:
                        return "";
                    }
                  })()}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">お問い合わせ内容</div>
                <div className="whitespace-pre-wrap text-gray-900">
                  {formData.message}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">
                  プライバシーポリシー同意
                </div>
                <div className="font-medium text-gray-900">
                  {formData.privacy_agreement ? "同意する" : "同意しない"}
                </div>
              </div>
            </div>

            {submitStatus === "error" && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                送信に失敗しました。時間をおいて再度お試しください。
              </div>
            )}

            <div className="flex flex-col md:flex-row gap-4 mt-4">
              <button
                type="button"
                onClick={handleReset}
                className="w-full md:w-1/2 border border-gray-300 text-gray-800 py-3 rounded-lg font-medium hover:bg-gray-50 transition-all cursor-pointer"
              >
                戻って修正する
              </button>
              <button
                type="button"
                onClick={handleFinalSubmit}
                disabled={isSubmitting}
                className="w-full md:w-1/2 bg-gray-900 text-white py-3 rounded-lg font-medium hover:bg-gray-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {isSubmitting ? "送信中..." : "この内容で送信する"}
              </button>
            </div>
          </div>
        )}

        {/* ===== 完了ステップ ===== */}
        {step === "done" && (
          <div className="text-center bg-gray-50 border border-gray-200 rounded-lg p-10">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-teal-100 flex items-center justify-center">
              <i className="ri-check-line text-2xl text-teal-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              送信が完了しました
            </h3>
            <p className="text-gray-600 mb-6">
              お問い合わせありがとうございます。担当者より折り返しご連絡いたします。
            </p>
            <button
              type="button"
              onClick={handleReset}
              className="px-6 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-all cursor-pointer"
            >
              もう一度送信する
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

/* ================================
 *  ページ全体コンポーネント
 * ================================ */
export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "bg-white shadow-md" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div
                className={`text-2xl font-bold transition-colors ${
                  isScrolled ? "text-gray-900" : "text-white"
                }`}
              >
                FILLS
              </div>
              <div
                className={`text-xs transition-colors ${
                  isScrolled ? "text-gray-600" : "text-white/80"
                }`}
              >
                合同会社
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a
                href="#home"
                className={`text-sm transition-colors hover:opacity-70 whitespace-nowrap cursor-pointer ${
                  isScrolled ? "text-gray-700" : "text-white"
                }`}
              >
                ホーム
              </a>
              <a
                href="#about"
                className={`text-sm transition-colors hover:opacity-70 whitespace-nowrap cursor-pointer ${
                  isScrolled ? "text-gray-700" : "text-white"
                }`}
              >
                会社概要
              </a>
              <a
                href="#business"
                className={`text-sm transition-colors hover:opacity-70 whitespace-nowrap cursor-pointer ${
                  isScrolled ? "text-gray-700" : "text-white"
                }`}
              >
                事業内容
              </a>
              <a
                href="#services"
                className={`text-sm transition-colors hover:opacity-70 whitespace-nowrap cursor-pointer ${
                  isScrolled ? "text-gray-700" : "text-white"
                }`}
              >
                サービス
              </a>
              <a
                href="#contact"
                className={`text-sm transition-colors hover:opacity-70 whitespace-nowrap cursor-pointer ${
                  isScrolled ? "text-gray-700" : "text-white"
                }`}
              >
                採用・お問い合わせ
              </a>
              <a
                href="#access"
                className={`text-sm transition-colors hover:opacity-70 whitespace-nowrap cursor-pointer ${
                  isScrolled ? "text-gray-700" : "text-white"
                }`}
              >
                アクセス
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section
        id="home"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20digital%20network%20technology%20background%20with%20flowing%20data%20streams%20and%20geometric%20patterns%20in%20soft%20teal%20and%20gray%20gradient%20colors%20representing%20modern%20e-commerce%20and%20internet%20business%20connectivity%20with%20minimalist%20professional%20aesthetic%20clean%20composition&width=1920&height=1080&seq=hero-bg-001&orientation=landscape"
            alt="Digital Business Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12">
            <div className="flex-1 w-full">
              <div className="flex items-center gap-6 mb-8">
                <p className="text-sm text-white/90 max-w-xs">
                  大阪市東成区を拠点とする信頼のデジタルマーケティング企業
                </p>
              </div>

              <h1 className="text-5xl md:text-7xl font-bold text-white mb-4 leading-tight">
                デジタルで、
                <br />
                <span className="font-light">ビジネスの未来を創る</span>
              </h1>

              <a
                href="#services"
                className="inline-flex items-center gap-3 bg-white text-gray-900 px-8 py-4 rounded-full font-medium hover:bg-gray-100 transition-all mt-8 whitespace-nowrap cursor-pointer"
              >
                サービス詳細を見る
                <div className="w-8 h-8 rounded-full bg-gray-900 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-white text-sm"></i>
                </div>
              </a>
            </div>

            <div className="flex-1 flex justify-end w-full">
              <p className="text-lg md:text-xl text-white max-w-md leading-relaxed">
                ECサイト構築から広告運用まで、インターネットビジネスの総合支援
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative h-96 overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20professional%20business%20office%20workspace%20with%20digital%20devices%20laptop%20and%20tablet%20on%20desk%20showing%20e-commerce%20dashboard%20bright%20natural%20lighting%20clean%20minimalist%20environment%20representing%20successful%20digital%20business%20operations&width=1920&height=600&seq=cta-bg-002&orientation=landscape"
            alt="Business Office"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div className="relative z-10 h-full flex flex-col justify-between max-w-7xl mx-auto px-6 py-12">
          <div className="flex justify-start">
            <h2 className="text-5xl md:text-6xl font-bold text-white max-w-xl leading-tight">
              令和5年設立、
              <br />
              確かな実績
            </h2>
          </div>

          <div className="flex justify-between items-end">
            <a
              href="#contact"
              className="inline-flex items-center gap-3 bg-white text-gray-900 px-8 py-4 rounded-full font-medium hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
            >
              お問い合わせ
              <div className="w-8 h-8 rounded-full bg-gray-900 flex items-center justify-center">
                <i className="ri-arrow-right-line text-white text-sm"></i>
              </div>
            </a>

            <p className="text-lg md:text-xl text-white max-w-md leading-relaxed text-right">
              銀行取引にも対応できる、
              <br />
              透明性の高い企業運営を実現
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid Section */}
      <section id="services" className="py-24 bg-[#F5F3F0]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-start mb-16">
            <div className="text-sm text-gray-600 leading-tight">
              FILLS
              <br />
              LLC
            </div>
            <h2 className="text-5xl font-serif text-gray-900">提供サービス</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* EC Card */}
            <div className="bg-[#1E3A5F] rounded-3xl p-8 shadow-lg">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mb-6">
                <span className="text-white font-bold text-sm">EC</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">
                ECサイト構築・運営
              </h3>
              <p className="text-gray-300 text-sm">
                ショッピングモール出店から独自サイト開設まで
              </p>
            </div>

            {/* Marketing Card */}
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="inline-flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full mb-4">
                <i className="ri-megaphone-line text-xs text-gray-600"></i>
                <span className="text-xs text-gray-600 font-medium">
                  MARKETING
                </span>
              </div>
              <div className="w-full h-40 mb-6 rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=digital%20marketing%20advertising%20dashboard%20with%20analytics%20graphs%20and%20social%20media%20icons%20on%20computer%20screen%20showing%20campaign%20performance%20metrics%20colorful%20data%20visualization%20modern%20professional%20interface%20clean%20background&width=400&height=300&seq=marketing-img-003&orientation=landscape"
                  alt="Digital Marketing"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                広告・マーケティング
              </h3>
              <p className="text-gray-600 text-sm">効果的な集客戦略を提案</p>
            </div>

            {/* Research Card */}
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <h3 className="text-2xl font-serif text-gray-900 mb-3">
                情報検索代行
              </h3>
              <p className="text-gray-600 text-sm mb-6">専門的なリサーチ業務</p>
              <div className="w-full h-40 rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=business%20research%20and%20data%20analysis%20with%20documents%20reports%20and%20magnifying%20glass%20on%20desk%20professional%20market%20research%20environment%20organized%20workspace%20clean%20minimalist%20background&width=400&height=300&seq=research-img-004&orientation=landscape"
                  alt="Business Research"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="w-full h-[500px] rounded-[35px] overflow-hidden shadow-xl">
              <img
                src="img/23856425_s.jpg"
                alt="FILLS Office"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div>
              <div className="inline-block bg-blue-50 px-4 py-2 rounded-lg mb-6">
                <span className="text-sm font-medium text-blue-900">
                  ABOUT US
                </span>
              </div>
              <h2 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
                FILLS合同会社
                <br />
                について
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed mb-8">
                <p>
                  <strong className="text-gray-900">会社名：</strong>
                  FILLS合同会社
                </p>
                <p>
                  <strong className="text-gray-900">所在地：</strong>
                  大阪府大阪市東成区玉津2丁目11-27-505
                </p>
                <p>
                  <strong className="text-gray-900">設立日：</strong>
                  令和5年10月5日
                </p>
                <p className="pt-4">
                  インターネットを活用した通信販売、ECサイト運営、デジタル広告、マーケティング事業を展開しています。法人格を持つ信頼性の高い企業として、銀行取引にも対応可能な透明性の高い経営を実現しています。
                </p>
              </div>
              <a
                href="#business"
                className="inline-flex items-center gap-3 bg-gray-100 text-gray-900 px-6 py-3 rounded-lg hover:bg-gray-200 transition-all whitespace-nowrap cursor-pointer"
              >
                会社情報詳細
                <i className="ri-arrow-right-up-line text-lg"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Business Content Section */}
      <section id="business" className="py-24 bg-[#F5F3F0]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-start mb-16">
            <div className="text-sm text-gray-600 leading-tight">FILLS</div>
            <h2 className="text-5xl font-serif text-gray-900">事業内容</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Feature 1 */}
            <div className="bg-gray-900 rounded-3xl p-8 shadow-lg">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mb-6">
                <span className="text-white font-bold text-sm">01</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">
                通信販売事業
              </h3>
              <p className="text-gray-300 text-sm">
                インターネットを利用した各種商品の販売及びECサイトの開設・運営
              </p>
            </div>

            {/* Feature 2 */}
            <div className="relative rounded-3xl overflow-hidden shadow-lg h-80">
              <img
                src="https://readdy.ai/api/search-image?query=Yachiyo%20city%20Chiba%20prefecture%20Japan%20cityscape%20with%20modern%20buildings%20and%20green%20spaces%20aerial%20view%20local%20business%20district%20peaceful%20suburban%20environment%20clean%20organized%20urban%20planning&width=400&height=500&seq=yachiyo-img-006&orientation=portrait"
                alt="Yachiyo City"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
              <div className="absolute top-6 left-6">
                <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full border border-white/30">
                  <i className="ri-map-pin-line text-xs text-white"></i>
                  <span className="text-xs text-white font-medium">LOCAL</span>
                </div>
              </div>
              <div className="absolute bottom-6 left-6">
                <h3 className="text-2xl font-bold text-white">
                  地域密着型サポート
                </h3>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <h3 className="text-2xl font-serif text-gray-900 mb-3">
                広告代理事業
              </h3>
              <p className="text-gray-600 text-sm mb-6">
                インターネットメディア広告代理及びマーケティング支援
              </p>
              <div className="w-full h-40 rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=integrated%20digital%20services%20network%20diagram%20showing%20e-commerce%20advertising%20and%20search%20services%20connected%20with%20flowing%20lines%20modern%20infographic%20style%20clean%20professional%20visualization%20simple%20background&width=400&height=300&seq=services-img-007&orientation=landscape"
                  alt="Integrated Services"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>

          <div className="mt-12 bg-white rounded-3xl p-8 shadow-lg">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              主要事業領域
            </h3>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-teal-600 text-xl mt-0.5"></i>
                <span>インターネットを利用した通信販売</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-teal-600 text-xl mt-0.5"></i>
                <span>インターネットショッピングモールへの出店及び運営</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-teal-600 text-xl mt-0.5"></i>
                <span>
                  インターネットを利用した各種商品の販売及びECサイトの開設並びに運営
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-teal-600 text-xl mt-0.5"></i>
                <span>インターネットを利用した広告及びマーケティング事業</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-teal-600 text-xl mt-0.5"></i>
                <span>インターネットを利用した情報検索代行業務</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-teal-600 text-xl mt-0.5"></i>
                <span>インターネットメディア広告代理事業</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Contact Section（さっき定義したやつ） */}
      <ContactSection />

      {/* Access Section */}
      <section id="access" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">
            アクセス
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                所在地
              </h3>
              <div className="space-y-4 text-gray-700">
                <div className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-teal-600 text-xl mt-1"></i>
                  <div>
                    <p className="font-medium">FILLS合同会社</p>
                    <p>〒537-0023</p>
                    <p>大阪府大阪市東成区玉津2丁目11-27-505</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <i className="ri-information-line text-teal-600 text-xl mt-1"></i>
                  <div>
                    <p className="font-medium">ご案内</p>
                    <p>詳細なアクセス方法はお問い合わせ時にご案内いたします。</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full h-96 rounded-2xl overflow-hidden shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3281.462942548832!2d135.53265947620835!3d34.668263772931184!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6000e0a60d000001%3A0x6056fc275193af1f!2z44CSNTM3LTAwMjMg5aSn6Ziq5bqc5aSn6Ziq5biC5p2x5oiQ5Yy6546J5rSl77yS5LiB55uu77yR77yR4oiS77yS77yX!5e0!3m2!1sja!2sjp!4v1774412180995!5m2!1sja!2sjp"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="FILLS合同会社 所在地"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1E3A5F] text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            {/* Company Info */}
            <div>
              <div className="text-2xl font-bold mb-4">FILLS</div>
              <div className="space-y-2 text-sm text-gray-300">
                <p>大阪府大阪市東成区玉津</p>
                <p>令和5年設立</p>
                <p>信頼のデジタルパートナー</p>
              </div>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-sm font-bold mb-4 uppercase tracking-wider">
                サービス
              </h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li>
                  <a
                    href="#services"
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    EC構築
                  </a>
                </li>
                <li>
                  <a
                    href="#services"
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    広告運用
                  </a>
                </li>
                <li>
                  <a
                    href="#services"
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    情報検索
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-600 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-gray-400">
                © 2023 FILLS合同会社. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
